/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import * as go from 'gojs';

import { NODE_STYLES } from '../graph-config';
import { GoMake } from './shared';

/**
 * Synthetic placeholder node rendered in the next-turn slot while pagination
 * fetches more turns. Visually just a concentric pulse (PULSE_RING +
 * PULSE_DOT); animation is driven by startTurnPaginationAnimation in turn-node.
 * Sized to the same bounding box as a real turn's outer ring so the connecting
 * link from the previous turn lands cleanly.
 *
 * Positioning: `locationSpot: Center` + `locationObjectName: 'LOADER_HOST'`
 * makes the bound location refer to the center of the invisible 32x32 host
 * circle. Real turn nodes' OUTER_RING centers end up at the same graph coord
 * as `${TURN_X} ${y}` (the y math in transform.ts assumes that), so anchoring
 * to LOADER_HOST's center keeps the pulse aligned with where the new turn
 * will materialize — independent of the loader panel's overall bbox.
 */
export function createTurnLoaderNodeTemplate($: GoMake): go.Node {
  return $(
    go.Node,
    'Spot',
    {
      selectionAdorned: false,
      pickable: false,
      locationSpot: go.Spot.Center,
      locationObjectName: 'LOADER_HOST',
      layoutConditions: go.LayoutConditions.Added | go.LayoutConditions.Removed,
    },
    new go.Binding('location', 'location', go.Point.parse, go.Point.stringify),
    // Invisible sizing shape — matches the real turn's outer-ring bounding box
    // so the link from the previous turn snaps at the same offset. Named
    // LOADER_HOST so locationObjectName can anchor the node's location to its
    // center.
    $(go.Shape, 'Circle', {
      name: 'LOADER_HOST',
      desiredSize: NODE_STYLES.turn.outerRing.size,
      fill: 'transparent',
      stroke: 'transparent',
      strokeWidth: 0,
    }),
    // PULSE_RING — animated scale + opacity at runtime.
    $(go.Shape, 'Circle', {
      name: 'PULSE_RING',
      desiredSize: NODE_STYLES.turn.pulseRing.size,
      fill: NODE_STYLES.turn.pulseRing.fill,
      stroke: NODE_STYLES.turn.pulseRing.stroke,
      strokeWidth: NODE_STYLES.turn.pulseRing.strokeWidth,
      alignment: go.Spot.Center,
      isPanelMain: false,
    }),
    // PULSE_DOT — centered indicator.
    $(go.Shape, 'Circle', {
      name: 'PULSE_DOT',
      desiredSize: NODE_STYLES.turn.pulseDot.size,
      fill: NODE_STYLES.turn.pulseDot.fill,
      stroke: NODE_STYLES.turn.pulseDot.stroke,
      strokeWidth: NODE_STYLES.turn.pulseDot.strokeWidth,
      alignment: go.Spot.Center,
      isPanelMain: false,
    }),
    // Top port — incoming link from the previous turn snaps here.
    $(go.Shape, 'Circle', {
      width: 2,
      height: 2,
      fill: 'transparent',
      stroke: 'transparent',
      portId: 'top',
      alignment: go.Spot.Top,
      fromLinkable: false,
      toLinkable: true,
    }),
    // Bottom port — outgoing link to the end node snaps here.
    $(go.Shape, 'Circle', {
      width: 2,
      height: 2,
      fill: 'transparent',
      stroke: 'transparent',
      portId: 'bottom',
      alignment: go.Spot.Bottom,
      fromLinkable: true,
      toLinkable: false,
    })
  );
}
